import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Modal,
  TextField,
  Typography,
} from '@mui/material';

import LoadingButton from '../../../components/buttons/Button';
import useSearchableSelect from '../../../components/hooks/useSearchableSelect';

import { CreateDOTLicenseBatchCERT } from '../../../redux/actions/certification/licenseBatchActions';
import { GetAuthoritiesDropdownCERT } from '../../../redux/actions/certification/authoritiesActions';
import { GET_AUTHORITIES_DROPDOWN_CERT } from '../../../redux/actionTypes';

// import global from "../../../global.scss";

const Data = {
  authority: null,
  total_licenses: '',
  notes: '',
};

const AllocateLicensesModal = ({
  open,
  setOpen,
  handleGet,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);

  // ── Searchable select: Authorities ─────────────────────────────────────
  const {
    results: authorityResults,
    loading: authoritySearchLoading,
    handleSearch: handleAuthoritySearch,
    reset: resetAuthoritySearch,
  } = useSearchableSelect(
    GetAuthoritiesDropdownCERT,
    (state) => {
      const list = state.certification.authoritiesDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    resetAuthoritySearch();
    dispatch({ type: GET_AUTHORITIES_DROPDOWN_CERT, payload: null });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isValid = formData.authority && Number(formData.total_licenses) > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    const obj = {
      authority_id: formData.authority.id,
      authority_name: formData.authority.authority_name,
      total_licenses: Number(formData.total_licenses),
      notes: formData.notes.trim(),
    };
    dispatch(CreateDOTLicenseBatchCERT(obj, setLoading)).then(({ statusCode }) => {
      if (statusCode === 201) {
        handleGet && handleGet();
        handleClose();
      }
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="allocate-licenses-modal-title">
      <Box
        className="flexCenterCenterRow"
        width={"100vw"}
        height={"100vh"}>
        <Box
          minWidth={"380px"}
          maxWidth={"560px"}
          width={"100%"}
          className="modal flex_SBColumn"
          sx={{ overflow: "hidden" }}>

          <Box sx={{ p: "20px 25px" }}>
            <Typography id="allocate-licenses-modal-title" variant='h6'>
              Allocate License Batch
            </Typography>
          </Box>

          <Divider />

          <Box
            className="flex_CenterColumn"
            gap={"20px"}
            sx={{ margin: "25px" }}>

            <Autocomplete
              fullWidth
              options={Array.isArray(authorityResults) ? authorityResults : []}
              loading={authoritySearchLoading}
              value={formData.authority}
              filterOptions={(x) => x}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') handleAuthoritySearch(value);
              }}
              onChange={(e, value) => setFormData({ ...formData, authority: value || null })}
              getOptionLabel={(option) => option.authority_name || option.id || "NA"}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" color={"black"}>{option.authority_name || "NA"}</Typography>
                    <Typography variant="caption" sx={{ color: "grey" }}>
                      {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Authority"
                  placeholder="Search by name..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {authoritySearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Number of Licenses"
              fullWidth
              name="total_licenses"
              type="number"
              inputProps={{ min: 1, max: 10000 }}
              placeholder="e.g. 50"
              value={formData.total_licenses}
              onChange={handleChange}
            />

            <TextField
              label="Notes (optional)"
              fullWidth
              name="notes"
              multiline
              rows={2}
              placeholder="Internal notes about this batch..."
              value={formData.notes}
              onChange={handleChange}
            />

          </Box>

          <Divider />

          <Box
            className="flexCenterFERow"
            gap={"16px"}
            sx={{ p: "16px 20px" }}>
            <LoadingButton variant='text' onClick={handleClose}>
              Cancel
            </LoadingButton>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={!isValid}
              onClick={handleSubmit}>
              Create Batch
            </LoadingButton>
          </Box>

        </Box>
      </Box>
    </Modal>
  );
};

export default AllocateLicensesModal;
